import { signal } from '@preact/signals';

// Opening a file of the app itself in the installed app (home screen, display: standalone).
//
// On iOS a same-origin target="_blank" link or window.open() opens inside the app's own
// window, which has no back button: the user has to kill the app. In the installed app
// those files open in FileViewer instead, an overlay with a close button. In a normal
// browser tab nothing changes: a new tab, or the browser's own download.

export const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true;

// The file FileViewer shows: { url, name } or null.
export const viewerFile = signal(null);

// Replaces window.open(url) for a file of the app.
export function openFile(url, name) {
  if (!isStandalone()) {
    window.open(url);
    return;
  }
  viewerFile.value = { url, name: name || null };
}

// onClick for an <a href target="_blank"> to a file of the app. Modified clicks
// (cmd/ctrl, middle button) keep following the href.
export function fileLinkClick(e, name) {
  if (!isStandalone() || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  viewerFile.value = { url: e.currentTarget.getAttribute('href'), name: name || null };
}

// File name from a Content-Disposition header, e.g. `attachment; filename="a.pdf"; filename*=UTF-8''a%C3%AB.pdf`.
export function fileNameFromDisposition(header) {
  if (!header) return null;
  const star = /filename\*\s*=\s*UTF-8'[^']*'([^;]+)/i.exec(header);
  if (star) {
    try { return decodeURIComponent(star[1].trim()); } catch { /* fall through to filename= */ }
  }
  const plain = /filename\s*=\s*"([^"]*)"|filename\s*=\s*([^;]+)/.exec(header);
  const name = plain && (plain[1] ?? plain[2])?.trim();
  return name || null;
}

const EXTENSIONS = {
  'application/pdf': '.pdf', 'image/png': '.png', 'image/jpeg': '.jpg', 'image/gif': '.gif',
  'image/webp': '.webp', 'image/svg+xml': '.svg', 'text/csv': '.csv',
};

const extensionOf = name => /\.[a-z][a-z0-9]{1,4}$/i.exec(name || '')?.[0] || null;

// A name to save the file under: `name` from the link, else the server's `serverName`.
// Inline routes send no file name, and a file saved to Files without an extension can't
// be opened, so a missing one comes from the server's name or the mime type.
export function saveName(name, type, serverName = null) {
  const base = (name || '').trim() || (serverName || '').trim() || 'Bestand';
  if (extensionOf(base)) return base;
  const ext = extensionOf(serverName) || EXTENSIONS[(type || '').split(';')[0].trim().toLowerCase()];
  return ext ? base + ext : base;
}

// What the viewer does with a file of this mime type.
export function fileKind(type) {
  const t = (type || '').split(';')[0].trim().toLowerCase();
  if (t === 'application/pdf') return 'pdf';
  if (['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(t)) return 'image';
  return 'other';
}
